import { ITeamMember, ROLE_INFO, deriveRoleFromPermissions } from '@/@types/TeamMember';
import Badge from '@/components/shared/Badge/Badge';
import Loader from '@/components/Loader/Loader';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/hooks/use-toast';
import LayoutInside from '@/layout/LayoutInside';
import { cn } from '@/lib/utils';
import { TeamService } from '@/services/team.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { pt } from 'date-fns/locale';
import { motion } from 'framer-motion';
import {
  Edit,
  Link2,
  Mail,
  MoreVertical,
  Plus,
  Search,
  Shield,
  Trash2,
  UserPlus,
  UserX,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditPermissionsSheet } from './Partials/EditPermissionsSheet';
import { InviteMemberSheet } from './Partials/InviteMemberSheet';
import { MemberCard } from './Partials/MemberCard';
import { RemoveMemberDialog } from './Partials/RemoveMemberDialog';
import { useTeamActions } from '@/hooks/useTeamActions';
import { useAuth } from '@/auth/Auth';

const MotionDiv = motion.div;

export default function Team() {
  const { t, i18n } = useTranslation('team');
  const queryClient = useQueryClient();
  const { authData } = useAuth();
  const isReadOnly = Boolean(authData?.read_only);

  const [searchQuery, setSearchQuery] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editMember, setEditMember] = useState<ITeamMember | null>(null);
  const [removeMember, setRemoveMember] = useState<ITeamMember | null>(null);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['team-members'],
    queryFn: TeamService.getMembers,
    staleTime: 1000 * 60 * 5,
  });

  const { categories } = useTeamActions();

  // Derivação de papéis a partir das permissões assim que as categorias são carregadas
  const membersWithDerivedRoles = members.map((member) => ({
    ...member,
    role: member.is_primary ? 'admin' as const : deriveRoleFromPermissions(member.permissions, categories),
  }));

  const { mutateAsync: resendInvite } = useMutation({
    mutationFn: TeamService.resendInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast({ description: t('invite_resent') });
    },
  });

  const copyInviteLink = async (inviteUrl: string) => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast({ description: t('link_copied') });
    } catch {
      toast({ variant: 'error', description: t('link_copy_error') });
    }
  };

  const { mutateAsync: cancelInvite } = useMutation({
    mutationFn: TeamService.cancelInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast({ description: t('invite_revoked') });
    },
  });

  const filteredMembers = membersWithDerivedRoles.filter((m) => {
    const query = searchQuery.toLowerCase();
    return (
      (m.name || '').toLowerCase().includes(query) ||
      m.email.toLowerCase().includes(query)
    );
  });

  const getTimeAgo = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const locale = i18n.language?.startsWith('pt') ? pt : undefined;
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale });
  };

  const breadcrumbItems = [
    { text: 'Dashboard', link: '/dashboard' },
    { text: t('team'), link: '/team' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <LayoutInside items={breadcrumbItems}>
      <InviteMemberSheet open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <EditPermissionsSheet
        open={!!editMember}
        onClose={() => setEditMember(null)}
        member={editMember}
      />
      <RemoveMemberDialog
        open={!!removeMember}
        onClose={() => setRemoveMember(null)}
        member={removeMember}
      />

      <section className="py-6">
        <MotionDiv
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6"
        >
          <div>
            <h1 className="text-xl font-bold">{t('team')}</h1>
            <p className="text-sm text-muted mt-1">{t('team_description')}</p>
          </div>
          <Button
            onClick={() => setInviteOpen(true)}
            className="gap-2 shrink-0"
            disabled={isReadOnly}
          >
            <Plus className="w-4 h-4" />
            {t('invite_member')}
          </Button>
        </MotionDiv>

        {isLoading ? (
          <div className="flex items-center justify-center h-[calc(100vh-300px)]">
            <Loader />
          </div>
        ) : membersWithDerivedRoles.length === 0 ? (
          <MotionDiv
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col items-center justify-center py-20 px-4"
          >
            <div className="w-20 h-20 rounded-full bg-elevated flex items-center justify-center mb-6">
              <Users className="w-10 h-10 text-muted" />
            </div>
            <h2 className="text-lg font-semibold mb-2">{t('no_members_title')}</h2>
            <p className="text-sm text-muted text-center max-w-md mb-6">
              {t('no_members_description')}
            </p>
            <Button
              onClick={() => setInviteOpen(true)}
              className="gap-2"
              disabled={isReadOnly}
            >
              <UserPlus className="w-4 h-4" />
              {t('invite_first_member')}
            </Button>
          </MotionDiv>
        ) : (
          <>
            <MotionDiv
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="mb-4"
            >
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('search_placeholder')}
                  className="w-full sm:w-80 h-10 pl-10 pr-4 rounded-lg border border-colorBorder bg-card text-sm text-colorText placeholder:text-placeholder focus:outline-none focus:border-primary/50 transition-colors"
                />
              </div>
            </MotionDiv>

            <div className="hidden md:block">
              <div className="border border-colorBorder rounded-lg overflow-hidden">
                <div className="bg-[#18181b]">
                  <div className="grid grid-cols-[1fr_1fr_120px_100px_60px] gap-4 px-4 py-3">
                    <span className="text-xs font-medium text-muted uppercase tracking-wider">
                      {t('member')}
                    </span>
                    <span className="text-xs font-medium text-muted uppercase tracking-wider">
                      {t('email')}
                    </span>
                    <span className="text-xs font-medium text-muted uppercase tracking-wider">
                      {t('role')}
                    </span>
                    <span className="text-xs font-medium text-muted uppercase tracking-wider">
                      {t('status')}
                    </span>
                    <span />
                  </div>
                </div>
                <MotionDiv
                  variants={containerVariants}
                  initial="hidden"
                  animate="show"
                >
                  {filteredMembers.map((member) => {
                    const roleInfo = ROLE_INFO[member.role];
                    return (
                      <MotionDiv
                        key={member.id}
                        variants={itemVariants}
                        className="grid grid-cols-[1fr_1fr_120px_100px_60px] gap-4 px-4 py-3 border-t border-colorBorder hover:bg-elevated/30 transition-colors items-center"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-elevated flex-shrink-0 flex items-center justify-center text-xs font-medium text-muted">
                            {(member.name || member.email).charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {member.name || '-'}
                            </p>
                            {member.last_access_at && (
                              <p className="text-[11px] text-muted truncate">
                                {t('last_access')}: {getTimeAgo(member.last_access_at)}
                              </p>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-muted truncate">{member.email}</p>
                        <div
                          className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium w-fit"
                          style={{
                            backgroundColor: `${roleInfo.color}1A`,
                            color: roleInfo.color,
                          }}
                        >
                          <Shield className="w-3 h-3" />
                          {t(`roles.${member.role}`)}
                        </div>
                        <Badge status={member.status} />
                        {!member.is_primary && !isReadOnly ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditMember(member)}>
                              <Edit className="w-4 h-4 mr-2" />
                              {t('edit_permissions')}
                            </DropdownMenuItem>
                            {member.status === 'pending' && member.latest_invite_id && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => member.latest_invite_url ? copyInviteLink(member.latest_invite_url) : undefined}
                                  disabled={!member.latest_invite_url}
                                >
                                  <Link2 className="w-4 h-4 mr-2" />
                                  {t('copy_invite_link')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => resendInvite(member.latest_invite_id!)}>
                                  <Mail className="w-4 h-4 mr-2" />
                                  {t('resend_invite')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => cancelInvite(member.latest_invite_id!)}>
                                  <UserX className="w-4 h-4 mr-2" />
                                  {t('revoke_invite')}
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem
                              onClick={() => setRemoveMember(member)}
                              className="text-red-400 focus:text-red-400"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t('remove')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        ) : <div />}
                      </MotionDiv>
                    );
                  })}
                </MotionDiv>
              </div>
              <p className="text-xs text-muted mt-3">
                {t('showing_members', { count: filteredMembers.length })}
              </p>
            </div>

            <MotionDiv
              variants={containerVariants}
              initial="hidden"
              animate="show"
              className="md:hidden space-y-3"
            >
              {filteredMembers.map((member) => (
                <MotionDiv key={member.id} variants={itemVariants}>
                  <MemberCard
                    member={member}
                    onEdit={setEditMember}
                    onRemove={setRemoveMember}
                    readOnly={isReadOnly}
                  />
                </MotionDiv>
              ))}
              <p className="text-xs text-muted mt-3">
                {t('showing_members', { count: filteredMembers.length })}
              </p>
            </MotionDiv>
          </>
        )}
      </section>
    </LayoutInside>
  );
}
